import { useDrop } from "react-dnd";
import { DraggableTab, ItemTypes } from "./DraggableTab";
import { MeasureDetailView, DataPoint } from "./MeasureDetailView";
import { SplitSquareHorizontal, X } from "lucide-react"; // Import X icon
import { DateRange } from "./DateRangeFilter";
import { ClinicalMeasurement } from "../../../../types";

interface Tab {
  id: string;
  measureName: string;
}

interface Pane {
  id: string;
  tabs: Tab[];
  activeTabId: string;
}

interface PaneContainerProps {
  pane: Pane;
  onTabClose: (paneId: string, tabId: string) => void;
  onTabClick: (paneId: string, tabId: string) => void;
  onMoveTab: (tabId: string, fromPaneId: string, toPaneId: string) => void;
  onSplit: (paneId: string) => void;
  onClosePane: (paneId: string) => void; // New Prop
  showSplit: boolean;
  globalDateRange: DateRange;
  measureDataMap: Record<string, ClinicalMeasurement>;
}

export function PaneContainer({
  pane,
  onTabClose,
  onTabClick,
  onMoveTab,
  onSplit,
  onClosePane, // Receive Prop
  showSplit,
  globalDateRange,
  measureDataMap,
}: PaneContainerProps) {
  const [{ isOver }, drop] = useDrop(() => ({
    accept: ItemTypes.TAB,
    drop: (item: { tabId: string; paneId: string }) => {
      if (item.paneId !== pane.id) {
        onMoveTab(item.tabId, item.paneId, pane.id);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  }));

  const activeTab = pane.tabs.find((tab) => tab.id === pane.activeTabId);

  return (
    <div
      ref={drop as unknown as React.LegacyRef<HTMLDivElement>}
      data-testid={`mbc-pane-container`}
      className="bg-white rounded-lg shadow-lg border relative flex flex-col h-full"
      style={{
        borderColor: isOver ? "#5A8DAB" : "#E2E8F0",
        borderWidth: isOver ? "2px" : "1px",
      }}
    >
      {/* Header Bar */}
      <div
        className="flex items-center justify-between border-b bg-slate-50 rounded-t-lg"
        style={{ borderColor: "#CBD5E1" }}
      >
        {/* Left: Tabs Area */}
        <div className="flex items-center gap-1 flex-1 min-h-[45px] overflow-x-auto px-2">
          {pane.tabs.map((tab) => (
            <DraggableTab
              key={tab.id}
              tabId={tab.id}
              tabLabel={tab.measureName}
              isActive={pane.activeTabId === tab.id}
              paneId={pane.id}
              onClick={() => onTabClick(pane.id, tab.id)}
              onClose={(e) => {
                e.stopPropagation();
                onTabClose(pane.id, tab.id);
              }}
            />
          ))}
        </div>

        {/* Right: Window Controls */}
        <div className="flex items-center px-2 border-l border-gray-200 h-[45px]">
          {/* Split Button */}
          {showSplit && (
            <button
              onClick={() => onSplit(pane.id)}
              className="p-2 hover:bg-white hover:shadow-sm rounded transition-all text-slate-600 hover:text-blue-600 mr-1"
              title="Split Pane"
            >
              <SplitSquareHorizontal size={18} />
            </button>
          )}

          {/* Close Pane Button */}
          <button
            onClick={() => onClosePane(pane.id)}
            data-testid="mbc-pane-close"
            className="p-2 hover:bg-red-50 hover:shadow-sm rounded transition-all text-slate-500 hover:text-red-500"
            title="Close Pane"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Detail View OR Empty State */}
      <div className="flex-1 bg-white rounded-b-lg overflow-hidden">
        {activeTab ? (
          <MeasureDetailView
            measureName={activeTab.measureName}
            longitudinalData={
              (measureDataMap[activeTab.measureName]?.longitudinalData ||
                []) as unknown as DataPoint[]
            }
            rawData={measureDataMap[activeTab.measureName]?.rawData || []}
            globalDateRange={globalDateRange}
            measureDataMap={measureDataMap}
          />
        ) : (
          <div className="h-full min-h-[400px] flex items-center justify-center text-slate-500 bg-slate-50/50">
            <div className="text-center border-2 border-dashed border-slate-300 rounded-xl p-8">
              <p className="text-sm font-medium">Drag a tab here</p>
            </div>
          </div>
        )}
      </div>

      {/* Drop zone overlay hint */}
      {isOver && (
        <div
          className="absolute inset-0 bg-blue-100 bg-opacity-20 border-2 rounded-lg pointer-events-none z-50"
          style={{ borderColor: "#5A8DAB" }}
        />
      )}
    </div>
  );
}
